/**
 * Shared conservative read-only SQL policy for every execution the user did
 * not explicitly click: the Query Editor / compact-VQE "Run on change" guard
 * (`query-editor/hooks/autoRunSafety.ts`) and the dashboard tile policy
 * (`electron/dashboards/executionPolicy.ts` — previews, untrusted scheduled
 * refreshes, and every cross-filtered run). ONE module, importable from both
 * the renderer and the main process: it pulls no Electron and no React/DOM
 * dependency, and the two callers must never grow their own denylists.
 *
 * Two layers, both required:
 *
 * 1. `classifyQueryForAi` — the strict head allowlist (SELECT / WITH / EXPLAIN
 *    …), the multi-statement refusal, and the top-level `INTO` / `FOR UPDATE`
 *    checks. It is handed the SAME dialect this module projects with, so its
 *    statement split is the engine's own (`projectCodeOnly` drives both).
 * 2. A dialect-aware scan of the CODE projection of the exact SQL
 *    (`projectCodeOnly`): comments and string literals are masked, MySQL
 *    executable comments (`/*!` … , which the server RUNS) are revealed, and
 *    any write / DDL / lock / procedure-call token, write-capable function,
 *    or native sequence advance anywhere in the projection refuses the run.
 *    This is what catches a data-modifying CTE (`WITH x AS (DELETE …
 *    RETURNING …) SELECT …`) and `SELECT … /*! INTO OUTFILE '…' *\/`, which
 *    layer 1 passes because it only inspects the head and strips block
 *    comments first. The function / package / sequence rules run over an
 *    identifier-REVEALING variant of that projection, so quoting
 *    (`"setval"`, `[xp_cmdshell]`, `` `get_lock` ``) and PostgreSQL's
 *    Unicode-escaped spelling (`U&"set\0076al"`) name the same call the
 *    server resolves.
 *
 * Without an engine the projection is taken under EVERY dialect and the
 * strictest answer wins. Conservative by design: a column literally named
 * `update` costs the user one click, a missed write costs them a row.
 *
 * SCOPE LIMIT — this is a policy over SQL TEXT, not a proof of harmlessness.
 * A read-shaped statement can still have side effects the text cannot show:
 * a user-defined function, view, or trigger invoked by the SELECT may write,
 * and so may a `SECURITY DEFINER` / autonomous-transaction routine. Only the
 * KNOWN write-capable built-ins listed below are recognised. Nothing here
 * substitutes for a read-only database role when side effects must be
 * impossible rather than merely unlikely; the docs must not claim otherwise.
 */
import type { DatabaseEngineId } from '../../core/engines';
import { classifyQueryForAi } from '../../core/engines/aiQueryClassifier';
import { safetyProjections } from '../sql-grammar/document/safetyProjections';
import type { SqlEngine } from '../sql-grammar/model';
import { revealQuotedIdentifiers } from './identifierReveal';
import { GRAMMAR_ENGINE_BY_ID } from './riskRules';

const ALL_ENGINES: readonly SqlEngine[] = ['mssql', 'postgres', 'mysql', 'oracle', 'sqlite'];

/**
 * Word boundary for SQL identifiers. `\b` is wrong here: `$` is not a word
 * character, so `\bLOCK\b` fires inside Oracle's `V$LOCK` and `\bBACKUP\b`
 * inside `V$BACKUP` — the dynamic performance views every DBA blocking-lock
 * and backup query selects FROM (it silently disqualified the shipped
 * `oracle.sessions.blocking_locks` dashboard template). `$` is an identifier
 * character in Oracle, MySQL and T-SQL, so it must count as one on both
 * sides; `.`, `@` and `#` deliberately still break the boundary, keeping
 * `pkg.LOCK` and `@delete` flagged.
 */
const NOT_IDENT_BEFORE = '(?<![\\w$])';
const NOT_IDENT_AFTER = '(?![\\w$])';

/** Tokens that never appear in a pure read but do in every nested write form. */
const WRITE_TOKEN = new RegExp(
  `${NOT_IDENT_BEFORE}(INSERT|UPDATE|DELETE|MERGE|UPSERT|INTO|OUTFILE|DUMPFILE|CREATE|ALTER|DROP|TRUNCATE|RENAME|GRANT|REVOKE|DENY|CALL|EXEC|EXECUTE|LOCK|UNLOCK|COMMIT|ROLLBACK|SAVEPOINT|KILL|SHUTDOWN|BACKUP|RESTORE|DBCC|BULK|OPENROWSET|OPENDATASOURCE|OPENQUERY)${NOT_IDENT_AFTER}`,
  'i',
);

/** Function calls that write, lock, sleep or reach outside the database. */
const WRITE_FUNCTION = new RegExp(
  `${NOT_IDENT_BEFORE}(nextval|setval|set_config|pg_terminate_backend|pg_cancel_backend|pg_advisory_\\w+|pg_try_advisory_\\w+|pg_reload_conf|pg_rotate_logfile|pg_sleep\\w*|lo_import|lo_export|lo_unlink|dblink\\w*|get_lock|release_lock|release_all_locks|sys_exec|sys_eval|sleep|benchmark|load_file|load_extension|writefile|xp_\\w+|sp_\\w+)\\s*\\(`,
  'i',
);

/**
 * Native sequence advancement — the write forms that carry NO function-call
 * parenthesis, so `WRITE_FUNCTION` never saw them (release review 1.5.0, R2
 * P1): Oracle's and MariaDB's `seq.NEXTVAL` pseudocolumn (quoted and
 * schema-qualified spellings included — the scan runs over the
 * identifier-revealing projection, where `seq."NEXTVAL"` reads `seq. NEXTVAL`)
 * and SQL Server's / MariaDB's `NEXT VALUE FOR seq`. Both CONSUME a value:
 * the sequence is permanently advanced for every other session, which is a
 * server-side side effect no unattended refresh may cause. `CURRVAL` and
 * `PREVIOUS VALUE FOR` only report the session's last value and stay allowed,
 * as do plain identifiers that merely contain the word (`nextval_log`,
 * `next_value`) — only the `.NEXTVAL` suffix and the three-word phrase match.
 */
const SEQUENCE_ADVANCE = new RegExp(
  `\\.\\s*NEXTVAL${NOT_IDENT_AFTER}|${NOT_IDENT_BEFORE}NEXT\\s+VALUE\\s+FOR${NOT_IDENT_AFTER}`,
  'i',
);

/** Oracle system packages (`dbms_lock.sleep`, `utl_file.fopen`, …): any reference is not read-only-safe. */
const ORACLE_SYSTEM_PACKAGE = new RegExp(
  `${NOT_IDENT_BEFORE}(dbms_\\w+|utl_\\w+)${NOT_IDENT_AFTER}`,
  'i',
);

function enginesFor(engineId: DatabaseEngineId | undefined): readonly SqlEngine[] {
  const engine = engineId ? GRAMMAR_ENGINE_BY_ID[engineId] : undefined;
  return engine ? [engine] : ALL_ENGINES;
}

/**
 * Why a statement may not auto-run, or `null` when it may. Exported for the
 * tests; the hook only needs the boolean.
 */
export function explainReadOnlySqlRefusal(sql: string, engineId?: DatabaseEngineId): string | null {
  const engines = enginesFor(engineId);
  // Layer 1 lexes with the SAME dialect(s) layer 2 projects: with an engine in
  // hand its multi-statement refusal is exact (a `;` inside a PostgreSQL
  // `$tag$…$tag$` body is not a separator), and without one it falls back to
  // "every dialect must agree" (R5 P1).
  const head = classifyQueryForAi(sql, engines.length === 1 ? engines[0] : undefined);
  if (!head.allowed) return head.reason ?? 'Not a read-only statement';
  for (const engine of engines) {
    const projections = safetyProjections(sql, engine);
    if (projections.every(projection => projection.ambiguous)) return `Could not parse the statement as ${engine}`;
    for (const projection of projections) {
      const token = WRITE_TOKEN.exec(projection.code);
      if (token) return `Contains ${token[1].toUpperCase()}, which can write or lock`;
      // Quoting a function, package or sequence cannot change the policy.
      const identifiers = revealQuotedIdentifiers(sql, projection.code);
      const fn = WRITE_FUNCTION.exec(identifiers);
      if (fn) return `Calls ${fn[1]}(), which is not read-only`;
      const pkg = ORACLE_SYSTEM_PACKAGE.exec(identifiers);
      if (pkg) return `References ${pkg[1].toUpperCase()}, which is not read-only`;
      if (SEQUENCE_ADVANCE.test(identifiers)) {
        return 'Advances a sequence (NEXTVAL / NEXT VALUE FOR), which is not read-only';
      }
    }
  }
  return null;
}

export function isReadOnlySql(sql: string, engineId?: DatabaseEngineId): boolean {
  return explainReadOnlySqlRefusal(sql, engineId) === null;
}
